import { DatePicker, Form, Input, Modal, notification } from 'antd'
import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import moment from 'moment'
import dayjs from 'dayjs'

import { useBlocktrainRules } from '@hooks'

import { Blocktrain, BlockTrainStatusEnum } from '../types'
import useUpdateBlocktrainStatus from '../hooks/blocktrain/useUpdateBlocktrainStatus'

interface BlocktrainModalProps {
  visible: boolean
  close: () => void
  blocktrain: Blocktrain
}

const ConfirmAuthorized: FC<BlocktrainModalProps> = ({ visible, close, blocktrain }) => {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation()
  const { updateBlocktrainStatus, updateBlocktrainStatusIsFetching } = useUpdateBlocktrainStatus()
  const blocktrainRules = useBlocktrainRules()

  const [form] = Form.useForm()

  const disabledDate = (current: any) => {
    return current && current < moment().startOf('day')
  }

  const initialValues = useMemo(
    () => ({
      estimatedTimeArrival: dayjs(blocktrain?.estimatedTimeArrival),
      estimatedDateOfDeparture: dayjs(blocktrain?.estimatedDateOfDeparture),
      railCarWayBill: blocktrain.railCarWayBill,
    }),
    [blocktrain],
  )

  const onFinishLoadingConfirm = async () => {
    // if (!blocktrain.railCarWayBill) {
    //   notification.error({
    //     message: t('globals.validation_error'),
    //     description: t('please_input_the_railwaybill'),
    //     key: 'railCarWayBillError',
    //   })
    //   return
    // }

    await form
      .validateFields()
      .then(values => {
        const { estimatedDateOfDeparture, estimatedTimeArrival, railCarWayBill } = values
        const payload = {
          id: id!,
          status: BlockTrainStatusEnum.TRANSIT,
          estimatedDateOfDeparture,
          estimatedTimeArrival,
          railCarWayBill,
        }
        updateBlocktrainStatus(payload)
        form.resetFields()
        close()
      })
      .catch(err => {
        notification.destroy('validationError')
        notification.error({
          message: t('globals.validation_error'),
          key: 'validationError',
        })
        console.error(err)
      })
  }

  return (
    <Modal
      title={t('put_blocktrain_in_transit')}
      open={visible}
      onCancel={() => {
        close()
      }}
      className="entity-modal"
      okText={t('set_in_transit')}
      cancelText={t('cancel')}
      onOk={onFinishLoadingConfirm}
      okButtonProps={{
        loading: updateBlocktrainStatusIsFetching,
      }}
    >
      <Form
        form={form}
        layout="horizontal"
        className="centered-form ant-form-item"
        initialValues={initialValues}
      >
        <Form.Item
          label={t('etd')}
          name="estimatedDateOfDeparture"
          initialValue={
            blocktrain?.estimatedDateOfDeparture
              ? moment(blocktrain.estimatedDateOfDeparture)
              : null
          }
          rules={[{ required: true, message: t('please_input_destination') }]}
        >
          <DatePicker
            placeholder={t('select_date')}
            disabledDate={disabledDate}
            style={{ width: '100%' }} // Ensure full width
            format="YYYY-MM-DD" // Explicitly set date format
          />
        </Form.Item>
        <Form.Item
          label={t('eta')}
          name="estimatedTimeArrival"
          initialValue={
            blocktrain?.estimatedTimeArrival ? moment(blocktrain.estimatedTimeArrival) : null
          }
          rules={[{ required: true, message: t('please_input_destination') }]}
        >
          <DatePicker
            placeholder={t('select_date')}
            disabledDate={disabledDate}
            style={{ width: '100%' }} // Ensure full width
            format="YYYY-MM-DD" // Explicitly set date format
          />
        </Form.Item>

        <Form.Item
          label={t('railWaybill_number')}
          name="railCarWayBill"
          rules={[...blocktrainRules.rayCarWayBill, { required: true }]}
        >
          <Input placeholder={t('input_railWaybill_number')} />
        </Form.Item>
      </Form>

      <h3>{t('cannot_undone_advice')}</h3>
    </Modal>
  )
}

export default ConfirmAuthorized
